import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {AdyenPaymentMethodsService} from '../services/payment-methods'
import {paymentMethodsConfiguration} from '../components/paymentMethodsConfiguration'
import {AdyenEnvironmentService} from '../services/environment'
import {onPaymentsDetailsSuccess, onPaymentsSuccess} from './helper'
import useUtils from '../../../custom-hooks/use-utils-helper'

export const AdyenCheckoutContext = React.createContext({})

const AdyenCheckoutProvider = ({
    children,
    authToken,
    customerEmail,
    customerId,
    isCustomerRegistered,
    preferredPayment,
    locale,
    site,
    adyenConfig,
    navigate,
    basket,
    returnUrl,
}) => {
    const [fetchingPaymentMethods, setFetchingPaymentMethods] = useState(false)
    const [adyenPaymentMethods, setAdyenPaymentMethods] = useState()
    const [adyenEnvironment, setAdyenEnvironment] = useState()
    const [adyenStateData, setAdyenStateData] = useState()
    const [adyenPaymentInProgress, setAdyenPaymentInProgress] = useState()

    useEffect(() => {
        const fetchEnvironment = async () => {
            const adyenEnvironmentService = new AdyenEnvironmentService(authToken, site)
            try {
                const data = await adyenEnvironmentService.fetchEnvironment()
                setAdyenEnvironment(data ? data : {error: true})
            } catch (error) {
                setAdyenEnvironment({error})
            }
        }
        fetchEnvironment()
    }, [])

    const {isCheckoutConfirmation} = useUtils().currentPage(location.pathname)

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            setFetchingPaymentMethods(true)
            const adyenPaymentMethodsService = new AdyenPaymentMethodsService(authToken, site)
            try {
                const data = await adyenPaymentMethodsService.fetchPaymentMethods(
                    customerId,
                    locale,
                    preferredPayment,
                )

                setAdyenPaymentMethods(data ? data : {error: true})
                setFetchingPaymentMethods(false)
            } catch (error) {
                setAdyenPaymentMethods({error})
                setFetchingPaymentMethods(false)
            }
        }

        if (!adyenPaymentMethods && !fetchingPaymentMethods && !isCheckoutConfirmation) {
            fetchPaymentMethods()
        }
    }, [basket?.basketId])

    const getTranslations = () => {
        return adyenConfig?.translations && Object.hasOwn(adyenConfig?.translations, locale.id)
            ? adyenConfig?.translations
            : null
    }

    const getPaymentMethodsConfiguration = async ({
        beforeSubmit = [],
        afterSubmit = [],
        beforeAdditionalDetails = [],
        afterAdditionalDetails = [],
        onError,
    }) => {
        return paymentMethodsConfiguration({
            additionalPaymentMethodsConfiguration: adyenConfig?.paymentMethodsConfiguration,
            paymentMethods: adyenPaymentMethods?.paymentMethods,
            isCustomerRegistered,
            token: authToken,
            site,
            basket,
            returnUrl,
            customerEmail,
            customerId,
            onError: adyenConfig?.onError || onError,
            onNavigate: navigate,
            locale: locale.id,
            afterSubmit: [
                ...afterSubmit,
                ...(adyenConfig?.afterSubmit || []),
                onPaymentsSuccess(navigate),
            ],
            beforeSubmit: [...beforeSubmit, ...(adyenConfig?.beforeSubmit || [])],
            afterAdditionalDetails: [
                ...afterAdditionalDetails,
                ...(adyenConfig?.afterAdditionalDetails || []),
                onPaymentsDetailsSuccess(navigate),
            ],
            beforeAdditionalDetails: [
                ...beforeAdditionalDetails,
                ...(adyenConfig?.beforeAdditionalDetails || []),
            ],
        })
    }

    const value = {
        adyenEnvironment,
        adyenPaymentMethods,
        adyenStateData,
        adyenPaymentInProgress,
        locale,
        navigate,
        setAdyenPaymentInProgress: (data) => setAdyenPaymentInProgress(data),
        setAdyenStateData: (data) => setAdyenStateData(data),
        getPaymentMethodsConfiguration,
        getTranslations,
    }

    return <AdyenCheckoutContext.Provider value={value}>{children}</AdyenCheckoutContext.Provider>
}

AdyenCheckoutProvider.propTypes = {
    children: PropTypes.any,
    authToken: PropTypes.string,
    customerEmail: PropTypes.string,
    customerId: PropTypes.string,
    isCustomerRegistered: PropTypes.bool,
    locale: PropTypes.any,
    site: PropTypes.any,
    adyenConfig: PropTypes.any,
    navigate: PropTypes.any,
    basket: PropTypes.any,
    returnUrl: PropTypes.string,
    preferredPayment: PropTypes.string,
}

export default AdyenCheckoutProvider
